import { useState, useEffect } from 'react'
import { useQuery, useQueryClient, useMutation } from 'react-query'
import { 
  Save, 
  RefreshCw, 
  Settings as SettingsIcon,
  Bell,
  Shield,
  Database,
  Wrench,
  Server,
  HardDrive,
  AlertTriangle,
  CheckCircle,
  Gift,
  Users,
  Percent,
  DollarSign,
  Smartphone,
  Plus,
  Trash2,
  Edit,
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Switch,
  Select,
  SimpleGrid,
  Box,
  Divider,
  useToast,
  FormControl,
  FormLabel,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Badge,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from '@chakra-ui/react'
import { cmsService } from '@services/api'

export default function SettingsPage() {
  const queryClient = useQueryClient()
  const [settings, setSettings] = useState({
    systemName: 'Daladala Smart',
    currency: 'TZS',
    timezone: 'Africa/Dar_es_Salaam',
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: false,
    maintenanceMode: false,
    autoBackup: true,
    cacheEnabled: true,
    debugMode: false
  })

  const [referralSettings, setReferralSettings] = useState({
    enabled: true,
    rewardType: 'discount_percentage',  // discount_percentage | discount_fixed | cashback
    referrerRewardValue: 10,            // what the person who shared gets
    refereeRewardValue: 10,             // what the new user gets
    maxUsesPerCode: 100,
    requireFirstPayment: true,          // reward only fires after first paid trip
    programDescription: 'Share your code with friends and you both get a reward on your next trip!',
  })
  
  const [ownerSettings, setOwnerSettings] = useState({
    allowedTabs: ['Dashboard', 'Analytics', 'Vehicles', 'Drivers', 'Timetables']
  })

  const [pricingSettings, setPricingSettings] = useState({
    baseFare: 2000,
    pricePerKm: 200,
    baseFareThresholdKm: 10,
    minimumFare: 2000,
    roundTripDiscountEnabled: false,
    roundTripDiscountPercentage: 0,
  })

  const [bookingCancellationSettings, setBookingCancellationSettings] = useState({
    enabled: true,
    allowedHours: 2,
    feePercentage: 10,
    requireReason: true,
    refundToWallet: true,
  })

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { isOpen: isMaintenanceOpen, onOpen: onMaintenanceOpen, onClose: onMaintenanceClose } = useDisclosure()
  const { isOpen: isPrefixModalOpen, onOpen: onPrefixModalOpen, onClose: onPrefixModalClose } = useDisclosure()
  const [editingPrefix, setEditingPrefix] = useState<any>(null)
  const [prefixForm, setPrefixForm] = useState({ prefix: '', paymentMethod: 'mpesa', operator: '' })
  const toast = useToast()

  // Fetch payment prefixes
  const { data: paymentPrefixes, isLoading: prefixesLoading } = useQuery(
    'payment-prefixes',
    () => cmsService.getPaymentPrefixes(),
    { retry: false }
  )

  // Fetch system settings
  const { data: settingsData } = useQuery(
    'settings',
    () => cmsService.getSettings(),
    { retry: false }
  )

  // Fetch owner dashboard settings
  const { data: ownerSettingsData } = useQuery(
    'owner-dashboard-settings',
    () => cmsService.getOwnerDashboardSettings(),
    { retry: false }
  )

  // Fetch referral settings
  const { data: referralSettingsData } = useQuery(
    'referral-settings',
    () => cmsService.getReferralSettings(),
    { retry: false }
  )

  // Fetch pricing settings
  const { data: pricingSettingsData } = useQuery(
    'pricing-settings',
    () => cmsService.getFareCalculatorSettings(),
    { retry: false }
  )

  const { data: bookingCancellationSettingsData } = useQuery(
    'booking-cancellation-settings',
    () => cmsService.getBookingCancellationSettings(),
    { retry: false }
  )

  useEffect(() => {
    if (ownerSettingsData?.data) {
      setOwnerSettings({
        allowedTabs: ownerSettingsData.data.allowedTabs || ['Dashboard', 'Analytics', 'Vehicles', 'Drivers', 'Timetables']
      })
    }
  }, [ownerSettingsData])

  useEffect(() => {
    if (referralSettingsData?.data && Object.keys(referralSettingsData.data).length > 0) {
      setReferralSettings(s => ({ ...s, ...referralSettingsData.data }))
    }
  }, [referralSettingsData])

  useEffect(() => {
    if (pricingSettingsData?.data && Object.keys(pricingSettingsData.data).length > 0) {
      setPricingSettings(s => ({ ...s, ...pricingSettingsData.data }))
    }
  }, [pricingSettingsData])

  useEffect(() => {
    if (bookingCancellationSettingsData?.data && Object.keys(bookingCancellationSettingsData.data).length > 0) {
      setBookingCancellationSettings((s) => ({ ...s, ...bookingCancellationSettingsData.data }))
    }
  }, [bookingCancellationSettingsData])

  useEffect(() => {
    if (settingsData?.data) {
      const data = settingsData.data;
      if (data.general) {
        setSettings(s => ({
          ...s,
          systemName: data.general.systemName || s.systemName,
          currency: data.general.currency || s.currency,
          timezone: data.general.timezone || s.timezone,
        }))
      }
      if (data.sms) {
        setSettings(s => ({
          ...s,
          smsNotifications: data.sms.enabled ?? s.smsNotifications
        }))
      }
      if (data.email) {
        setSettings(s => ({
          ...s,
          emailNotifications: data.email.enabled ?? s.emailNotifications
        }))
      }
      if (data.maintenance) {
        setSettings(s => ({
          ...s,
          maintenanceMode: data.maintenance.enabled ?? s.maintenanceMode
        }))
      }
    }
  }, [settingsData])

  const createPrefixMutation = useMutation(
    (data: any) => cmsService.createPaymentPrefix(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payment-prefixes')
        toast({ title: 'Prefix added', status: 'success', duration: 3000, isClosable: true })
        onPrefixModalClose()
        setPrefixForm({ prefix: '', paymentMethod: 'mpesa', operator: '' })
      },
      onError: (error: any) => {
        toast({ title: 'Failed to add prefix', description: error?.response?.data?.error ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const updatePrefixMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => cmsService.updatePaymentPrefix(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payment-prefixes')
        toast({ title: 'Prefix updated', status: 'success', duration: 3000, isClosable: true })
        setEditingPrefix(null)
        onPrefixModalClose()
      },
      onError: (error: any) => {
        toast({ title: 'Failed to update prefix', description: error?.response?.data?.error ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  // Settings mutations
  const generalSettingsMutation = useMutation(
    (data: any) => cmsService.updateGeneralSettings(data),
    {
      onSuccess: () => {
        toast({ title: 'General settings updated', status: 'success', duration: 3000, isClosable: true })
        queryClient.invalidateQueries('settings')
      },
      onError: (error: any) => {
        toast({ title: 'Failed to update general settings', description: error?.response?.data?.error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const smsSettingsMutation = useMutation(
    (data: any) => cmsService.updateSMSSettings(data),
    {
      onSuccess: () => {
        toast({ title: 'SMS settings updated', status: 'success', duration: 3000, isClosable: true })
        queryClient.invalidateQueries('settings')
      },
      onError: (error: any) => {
        toast({ title: 'Failed to update SMS settings', description: error?.response?.data?.error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const emailSettingsMutation = useMutation(
    (data: any) => cmsService.updateEmailSettings(data),
    {
      onSuccess: () => {
        toast({ title: 'Email settings updated', status: 'success', duration: 3000, isClosable: true })
        queryClient.invalidateQueries('settings')
      },
      onError: (error: any) => {
        toast({ title: 'Failed to update email settings', description: error?.response?.data?.error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const bookingCancellationSettingsMutation = useMutation(
    (data: any) => cmsService.updateBookingCancellationSettings(data),
    {
      onSuccess: () => {
        toast({ title: 'Booking cancellation settings updated', status: 'success', duration: 3000, isClosable: true })
        queryClient.invalidateQueries('booking-cancellation-settings')
      },
      onError: (error: any) => {
        toast({ title: 'Failed to update booking cancellation settings', description: error?.response?.data?.error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const referralSettingsMutation = useMutation(
    (data: any) => cmsService.updateReferralSettings(data),
    {
      onSuccess: () => {
        toast({ title: 'Referral settings updated', status: 'success', duration: 3000, isClosable: true })
        queryClient.invalidateQueries('referral-settings')
      },
      onError: (error: any) => {
        toast({ title: 'Failed to update referral settings', description: error?.response?.data?.error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const pricingSettingsMutation = useMutation(
    (data: any) => cmsService.updateFareCalculatorSettings(data),
    {
      onSuccess: () => {
        toast({ title: 'Pricing settings updated', status: 'success', duration: 3000, isClosable: true })
        queryClient.invalidateQueries('pricing-settings')
      },
      onError: (error: any) => {
        toast({ title: 'Failed to update pricing settings', description: error?.response?.data?.error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const maintenanceModeMutation = useMutation(
    () => cmsService.toggleMaintenanceMode(),
    {
      onSuccess: (data: any) => {
        setSettings({...settings, maintenanceMode: data.data.enabled})
        toast({ 
          title: data.data.enabled ? 'Maintenance Mode Enabled' : 'Maintenance Mode Disabled', 
          status: data.data.enabled ? 'warning' : 'success', 
          duration: 3000, 
          isClosable: true 
        })
      },
      onError: (error: any) => {
        toast({ title: 'Failed to toggle maintenance mode', description: error?.response?.data?.error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const backupMutation = useMutation(
    () => cmsService.createBackup(),
    {
      onSuccess: () => {
        toast({ title: 'Backup created successfully', status: 'success', duration: 3000, isClosable: true })
      },
      onError: (error: any) => {
        toast({ title: 'Failed to create backup', description: error?.response?.data?.error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const clearCacheMutation = useMutation(
    () => cmsService.clearCache(),
    {
      onSuccess: () => {
        toast({ title: 'Cache cleared successfully', status: 'success', duration: 3000, isClosable: true })
      },
      onError: (error: any) => {
        toast({ title: 'Failed to clear cache', description: error?.response?.data?.error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const deletePrefixMutation = useMutation(
    (id: string) => cmsService.deletePaymentPrefix(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payment-prefixes')
        toast({ title: 'Prefix deleted', status: 'success', duration: 3000, isClosable: true })
      },
      onError: (error: any) => {
        toast({ title: 'Failed to delete prefix', description: error?.response?.data?.error ?? 'Server error', status: 'error', duration: 5000, isClosable: true })
      }
    }
  )

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Parallel update of multiple settings sections
      await Promise.all([
        generalSettingsMutation.mutateAsync({
          systemName: settings.systemName,
          currency: settings.currency,
          timezone: settings.timezone
        }),
        smsSettingsMutation.mutateAsync({
          enabled: settings.smsNotifications,
          provider: 'beem', // Based on what we discussed earlier
          senderId: 'DALADALA'
        }),
        emailSettingsMutation.mutateAsync({
          enabled: settings.emailNotifications
        }),
        referralSettingsMutation.mutateAsync(referralSettings),
        pricingSettingsMutation.mutateAsync(pricingSettings)
      ])
    } catch (error) {
      // Errors are handled in mutation callbacks
    } finally {
      setIsSaving(false)
    }
  }

  const handleRefresh = () => {
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
      toast({
        title: 'Settings Refreshed',
        description: 'Settings have been refreshed from the server.',
        status: 'info',
        duration: 2000,
        isClosable: true,
      })
    }, 1000)
  }

  const handleMaintenanceToggle = () => {
    onMaintenanceOpen()
  }

  const confirmMaintenanceMode = () => {
    maintenanceModeMutation.mutate()
    onMaintenanceClose()
  }

  const handleBackup = () => {
    backupMutation.mutate()
  }

  const handleClearCache = () => {
    clearCacheMutation.mutate()
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={1} textAlign={{ base: 'center', md: 'left' }}>
          <Heading size={{ base: 'md', md: 'lg' }} color="gray.900">
            System Settings
          </Heading>
          <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
            Configure system preferences and maintenance
          </Text>
        </VStack>
        <HStack spacing={3} w={{ base: 'full', md: 'auto' }} justify={{ base: 'center', md: 'flex-end' }}>
          <Button
            leftIcon={<RefreshCw size={16} />}
            variant="outline"
            borderRadius="xl"
            borderColor="gray.300"
            color="gray.600"
            _hover={{ bg: 'gray.50' }}
            onClick={handleRefresh}
            isLoading={isLoading}
            loadingText="Refreshing..."
            size={{ base: 'sm', md: 'md' }}
            w={{ base: 'full', md: 'auto' }}
          >
            Refresh
          </Button>
          <Button
            leftIcon={<Save size={16} />}
            colorScheme="brand"
            borderRadius="xl"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
            size={{ base: 'sm', md: 'md' }}
            w={{ base: 'full', md: 'auto' }}
          >
            Save Changes
          </Button>
        </HStack>
      </Flex>

      {/* Settings Sections */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* General Settings */}
        <Card variant="glass">
          <CardHeader>
            <HStack spacing={3}>
              <Box p={2} bg="blue.100" borderRadius="lg">
                <SettingsIcon size={20} color="var(--chakra-colors-blue-600)" />
              </Box>
              <Heading size="md" color="gray.900">
                General Settings
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">System Name</FormLabel>
                <Input
                  value={settings.systemName}
                  onChange={(e) => setSettings({...settings, systemName: e.target.value})}
                  borderRadius="xl"
                  borderColor="gray.300"
                  _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Default Currency</FormLabel>
                <Select
                  value={settings.currency}
                  onChange={(e) => setSettings({...settings, currency: e.target.value})}
                  borderRadius="xl"
                  borderColor="gray.300"
                >
                <option value="TZS">Tanzanian Shilling (TZS)</option>
                <option value="USD">US Dollar (USD)</option>
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Timezone</FormLabel>
                <Select
                  value={settings.timezone}
                  onChange={(e) => setSettings({...settings, timezone: e.target.value})}
                  borderRadius="xl"
                  borderColor="gray.300"
                >
                <option value="Africa/Dar_es_Salaam">Africa/Dar es Salaam</option>
                <option value="UTC">UTC</option>
                </Select>
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        {/* Notification Settings */}
        <Card variant="glass">
          <CardHeader>
            <HStack spacing={3}>
              <Box p={2} bg="green.100" borderRadius="lg">
                <Bell size={20} color="var(--chakra-colors-green-600)" />
              </Box>
              <Heading size="md" color="gray.900">
                Notification Settings
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.900">
                    Email Notifications
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Send email notifications for important events
                  </Text>
                </VStack>
                <Switch
                  isChecked={settings.emailNotifications}
                  onChange={(e) => setSettings({...settings, emailNotifications: e.target.checked})}
                  colorScheme="brand"
                  size="lg"
                />
              </Flex>
              
              <Divider />
              
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.900">
                    SMS Notifications
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Send SMS notifications to users
                  </Text>
                </VStack>
                <Switch
                  isChecked={settings.smsNotifications}
                  onChange={(e) => setSettings({...settings, smsNotifications: e.target.checked})}
                  colorScheme="brand"
                  size="lg"
                />
              </Flex>
              
              {settings.smsNotifications && (
                <VStack spacing={3} pl={6} pt={2} align="stretch" borderLeft="2px solid" borderColor="gray.100">
                  <FormControl>
                    <FormLabel fontSize="xs">SMS Provider (Current: Beem Africa)</FormLabel>
                    <Select size="sm" borderRadius="lg" defaultValue="beem">
                      <option value="beem">Beem Africa</option>
                      <option value="twilio">Twilio (Future)</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Sender ID</FormLabel>
                    <Input size="sm" borderRadius="lg" placeholder="e.g. DALADALA" defaultValue="DALADALA" />
                  </FormControl>
                </VStack>
              )}
              
              <Divider />
              
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.900">
                    Push Notifications
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Send push notifications to mobile apps
                  </Text>
                </VStack>
                <Switch
                  isChecked={settings.pushNotifications}
                  onChange={(e) => setSettings({...settings, pushNotifications: e.target.checked})}
                  colorScheme="brand"
                  size="lg"
                />
              </Flex>
            </VStack>
          </CardBody>
        </Card>

        {/* System Maintenance */}
        <Card variant="glass">
          <CardHeader>
            <HStack spacing={3}>
              <Box p={2} bg="orange.100" borderRadius="lg">
                <Wrench size={20} color="var(--chakra-colors-orange-600)" />
              </Box>
              <Heading size="md" color="gray.900">
                System Maintenance
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={3} align="stretch">
              <Button
                leftIcon={<HardDrive size={16} />}
                colorScheme="orange"
                variant="outline"
                borderRadius="xl"
                onClick={handleBackup}
                _hover={{ bg: 'orange.50' }}
              >
              Create System Backup
              </Button>
              
              <Button
                leftIcon={<Database size={16} />}
                colorScheme="gray"
                variant="outline"
                borderRadius="xl"
                onClick={handleClearCache}
                _hover={{ bg: 'gray.50' }}
              >
              Clear Cache
              </Button>
              
              <Button
                leftIcon={<AlertTriangle size={16} />}
                colorScheme="red"
                variant="outline"
                borderRadius="xl"
                onClick={handleMaintenanceToggle}
                _hover={{ bg: 'red.50' }}
              >
                {settings.maintenanceMode ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
              </Button>
            </VStack>
          </CardBody>
        </Card>

        {/* System Information */}
        <Card variant="glass">
          <CardHeader>
            <HStack spacing={3}>
              <Box p={2} bg="purple.100" borderRadius="lg">
                <Server size={20} color="var(--chakra-colors-purple-600)" />
              </Box>
              <Heading size="md" color="gray.900">
                System Information
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={3} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color="gray.600">Version:</Text>
                <Badge colorScheme="blue" borderRadius="full">1.0.0</Badge>
              </Flex>
              
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color="gray.600">Last Updated:</Text>
                <Text fontSize="sm" fontWeight="medium">2025-01-27</Text>
              </Flex>
              
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color="gray.600">Database:</Text>
                <Badge colorScheme="green" borderRadius="full">PostgreSQL 15</Badge>
              </Flex>
              
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color="gray.600">Status:</Text>
                <HStack spacing={1}>
                  <CheckCircle size={12} color="var(--chakra-colors-green-500)" />
                  <Badge colorScheme="green" borderRadius="full">
                    Online
                  </Badge>
                </HStack>
              </Flex>
              
              <Divider />
              
              <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>System Health</Text>
                <Progress value={85} colorScheme="green" borderRadius="full" size="sm" />
                <Text fontSize="xs" color="gray.500" mt={1}>85% - Excellent</Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Pricing Settings */}
      <Card variant="glass">
        <CardHeader>
          <HStack spacing={3}>
            <Box p={2} bg="blue.100" borderRadius="lg">
              <DollarSign size={20} color="var(--chakra-colors-blue-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="gray.900">Pricing Configuration</Heading>
              <Text fontSize="sm" color="gray.500">Global defaults for fare calculation across all routes</Text>
            </VStack>
          </HStack>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium">Base Fare (TZS)</FormLabel>
              <NumberInput
                value={pricingSettings.baseFare}
                onChange={(_, val) => setPricingSettings({ ...pricingSettings, baseFare: val || 0 })}
                min={0}
                step={100}
              >
                <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs">Applied for distance up to threshold</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium">Base Distance Threshold (KM)</FormLabel>
              <NumberInput
                value={pricingSettings.baseFareThresholdKm}
                onChange={(_, val) => setPricingSettings({ ...pricingSettings, baseFareThresholdKm: val || 0 })}
                min={0}
                step={1}
              >
                <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs">Distance the base fare covers</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium">Price per extra KM (TZS)</FormLabel>
              <NumberInput
                value={pricingSettings.pricePerKm}
                onChange={(_, val) => setPricingSettings({ ...pricingSettings, pricePerKm: val || 0 })}
                min={0}
                step={50}
              >
                <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs">Added for every KM above threshold</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium">Absolute Minimum Fare (TZS)</FormLabel>
              <NumberInput
                value={pricingSettings.minimumFare}
                onChange={(_, val) => setPricingSettings({ ...pricingSettings, minimumFare: val || 0 })}
                min={0}
                step={100}
              >
                <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs">Hard minimum for any trip</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium">Round-Trip Discount</FormLabel>
              <Flex justify="space-between" align="center" p={4} bg="gray.50" borderRadius="xl">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.900">Enable round-trip discount</Text>
                  <Text fontSize="xs" color="gray.500">
                    Apply a percentage discount when the passenger books both outbound and return trips together.
                  </Text>
                </VStack>
                <Switch
                  isChecked={pricingSettings.roundTripDiscountEnabled}
                  onChange={(e) => setPricingSettings({ ...pricingSettings, roundTripDiscountEnabled: e.target.checked })}
                  colorScheme="green"
                />
              </Flex>
            </FormControl>

            <FormControl isDisabled={!pricingSettings.roundTripDiscountEnabled}>
              <FormLabel fontSize="sm" fontWeight="medium">Round-Trip Discount Percentage</FormLabel>
              <NumberInput
                value={pricingSettings.roundTripDiscountPercentage}
                onChange={(_, val) => setPricingSettings({ ...pricingSettings, roundTripDiscountPercentage: val || 0 })}
                min={0}
                max={100}
                step={1}
              >
                <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs">The discount is applied to the combined round-trip total before payment.</FormHelperText>
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      <Card variant="glass">
        <CardHeader>
          <HStack spacing={3}>
            <Box p={2} bg="red.100" borderRadius="lg">
              <Percent size={20} color="var(--chakra-colors-red-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="gray.900">Booking Cancellation Policy</Heading>
              <Text fontSize="sm" color="gray.500">Control when passengers can cancel, whether a reason is required, and how much is deducted before refunding to wallet.</Text>
            </VStack>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={5} align="stretch">
            <Flex justify="space-between" align="center" p={4} bg="gray.50" borderRadius="xl">
              <VStack align="start" spacing={1}>
                <Text fontWeight="semibold" color="gray.800">Enable passenger cancellation</Text>
                <Text fontSize="sm" color="gray.500">
                  If disabled, passengers cannot cancel bookings from the app.
                </Text>
              </VStack>
              <Switch
                isChecked={bookingCancellationSettings.enabled}
                onChange={(e) => setBookingCancellationSettings({ ...bookingCancellationSettings, enabled: e.target.checked })}
                colorScheme="red"
                size="lg"
              />
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Minimum hours before departure</FormLabel>
                <NumberInput
                  value={bookingCancellationSettings.allowedHours}
                  onChange={(_, val) => setBookingCancellationSettings({ ...bookingCancellationSettings, allowedHours: val || 0 })}
                  min={0}
                  step={1}
                >
                  <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText fontSize="xs">Passengers must cancel at least this many hours before the trip&apos;s scheduled first departure.</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Cancellation fee percentage</FormLabel>
                <NumberInput
                  value={bookingCancellationSettings.feePercentage}
                  onChange={(_, val) => setBookingCancellationSettings({ ...bookingCancellationSettings, feePercentage: val || 0 })}
                  min={0}
                  max={100}
                  step={1}
                >
                  <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText fontSize="xs">This percentage is deducted and the balance is returned to the passenger wallet.</FormHelperText>
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Flex justify="space-between" align="center" p={4} bg="gray.50" borderRadius="xl">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.900">Require cancellation reason</Text>
                  <Text fontSize="xs" color="gray.500">
                    Passengers must explain why they are cancelling.
                  </Text>
                </VStack>
                <Switch
                  isChecked={bookingCancellationSettings.requireReason}
                  onChange={(e) => setBookingCancellationSettings({ ...bookingCancellationSettings, requireReason: e.target.checked })}
                  colorScheme="brand"
                />
              </Flex>

              <Flex justify="space-between" align="center" p={4} bg="gray.50" borderRadius="xl">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.900">Refund to wallet</Text>
                  <Text fontSize="xs" color="gray.500">
                    Return the remaining amount to the passenger wallet after the fee is deducted.
                  </Text>
                </VStack>
                <Switch
                  isChecked={bookingCancellationSettings.refundToWallet}
                  onChange={(e) => setBookingCancellationSettings({ ...bookingCancellationSettings, refundToWallet: e.target.checked })}
                  colorScheme="green"
                />
              </Flex>
            </SimpleGrid>

            <Alert status="info" borderRadius="xl">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <AlertTitle fontSize="sm">Passenger flow</AlertTitle>
                <AlertDescription fontSize="xs">
                  If a paid ticket is cancelled within policy, the seat is reopened, the ticket becomes invalid for QR scanning, the deducted percentage is kept as a fee, and the rest is credited back to the passenger wallet.
                </AlertDescription>
              </VStack>
            </Alert>

            <Button
              alignSelf="flex-start"
              colorScheme="red"
              size="sm"
              onClick={() => bookingCancellationSettingsMutation.mutate(bookingCancellationSettings)}
              isLoading={bookingCancellationSettingsMutation.isLoading}
              loadingText="Saving..."
            >
              Save Cancellation Policy
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {/* Owner Dashboard Settings */}
      <Card variant="glass">
        <CardHeader>
          <HStack spacing={3}>
            <Box p={2} bg="teal.100" borderRadius="lg">
              <HardDrive size={20} color="var(--chakra-colors-teal-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="gray.900">Operator/Owner Dashboard</Heading>
              <Text fontSize="sm" color="gray.500">Configure which tabs are visible when a bus owner logs into the management portal.</Text>
            </VStack>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {['Dashboard', 'Analytics', 'Vehicles', 'Drivers', 'Trips', 'Timetables', 'Routes', 'Commuters', 'Bookings', 'Notifications', 'Settings'].map((tab) => (
                <Flex key={tab} justify="space-between" align="center" p={3} bg="gray.50" borderRadius="lg">
                  <Text fontSize="sm" fontWeight="medium" color="gray.900">{tab}</Text>
                  <Switch
                    isChecked={ownerSettings.allowedTabs.includes(tab)}
                    onChange={(e) => {
                      const newTabs = e.target.checked 
                        ? [...ownerSettings.allowedTabs, tab]
                        : ownerSettings.allowedTabs.filter(t => t !== tab);
                      setOwnerSettings({ allowedTabs: newTabs });
                    }}
                    colorScheme="brand"
                  />
                </Flex>
              ))}
            </SimpleGrid>
            <Button
              mt={4}
              alignSelf="flex-start"
              colorScheme="brand"
              onClick={async () => {
                try {
                  setIsSaving(true);
                  await cmsService.updateOwnerDashboardSettings({ allowedTabs: ownerSettings.allowedTabs });
                  toast({ title: 'Owner dashboard settings updated', status: 'success', duration: 3000, isClosable: true });
                } catch (error) {
                  toast({ title: 'Update failed', status: 'error', duration: 3000, isClosable: true });
                } finally {
                  setIsSaving(false);
                }
              }}
              isLoading={isSaving}
              loadingText="Saving..."
              size="sm"
            >
              Save Dashboard Settings
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {/* ── Referral Program Settings ─────────────────────────────── */}
      <Card variant="glass">
        <CardHeader>
          <HStack spacing={3}>
            <Box p={2} bg="pink.100" borderRadius="lg">
              <Gift size={20} color="var(--chakra-colors-pink-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="gray.900">Referral Program</Heading>
              <Text fontSize="sm" color="gray.500">Configure how users earn rewards by referring friends</Text>
            </VStack>
            <Badge
              ml="auto"
              colorScheme={referralSettings.enabled ? 'green' : 'gray'}
              borderRadius="full"
              px={3}
              py={1}
            >
              {referralSettings.enabled ? 'Active' : 'Paused'}
            </Badge>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">

            {/* Enable / disable toggle */}
            <Flex justify="space-between" align="center" p={4} bg="gray.50" borderRadius="xl">
              <VStack align="start" spacing={1}>
                <Text fontWeight="semibold" color="gray.800">Enable Referral Program</Text>
                <Text fontSize="sm" color="gray.500">
                  When disabled, new codes cannot be used but existing ones remain valid
                </Text>
              </VStack>
              <Switch
                isChecked={referralSettings.enabled}
                onChange={(e) => setReferralSettings({ ...referralSettings, enabled: e.target.checked })}
                colorScheme="green"
                size="lg"
              />
            </Flex>

            <Divider />



            {/* Reward type */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Reward Type</FormLabel>
                <Select
                  value={referralSettings.rewardType}
                  onChange={(e) => setReferralSettings({ ...referralSettings, rewardType: e.target.value })}
                  borderRadius="xl"
                  borderColor="gray.300"
                  bg="white"
                >
                  <option value="discount_percentage">Percentage off trip (referrer reward, e.g. 10%)</option>
                  <option value="discount_fixed">Fixed TZS off trip (referrer reward)</option>
                  <option value="free_trip">Free trip for referrer (100% of fare)</option>
                  <option value="cashback">Fixed TZS wallet credit (referrer — not a %)</option>
                </Select>
                <FormHelperText fontSize="xs">
                  Referrer reward type for the person who shared their code. Recommended: percentage or fixed discount. Wallet credit is a flat TZS amount, not a percent.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Max Uses Per Code</FormLabel>
                <NumberInput
                  value={referralSettings.maxUsesPerCode}
                  onChange={(_, val) => setReferralSettings({ ...referralSettings, maxUsesPerCode: val || 100 })}
                  min={1}
                  max={10000}
                >
                  <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText fontSize="xs">How many friends can use each person's code</FormHelperText>
              </FormControl>
            </SimpleGrid>

            {/* Reward values */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  <HStack spacing={2}>
                    <Users size={14} />
                    <span>Referrer Reward</span>
                    <Badge colorScheme="blue" fontSize="xs">Person who shared</Badge>
                  </HStack>
                </FormLabel>
                <HStack>
                  <NumberInput
                    value={referralSettings.referrerRewardValue}
                    onChange={(_, val) => setReferralSettings({ ...referralSettings, referrerRewardValue: val || 0 })}
                    min={0}
                    flex={1}
                  >
                    <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Badge colorScheme="purple" fontSize="sm" px={3} py={2}>
                    {referralSettings.rewardType === 'discount_percentage' ? '%' : 'TZS'}
                  </Badge>
                </HStack>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">
                  <HStack spacing={2}>
                    <Gift size={14} />
                    <span>Referee Reward</span>
                    <Badge colorScheme="green" fontSize="xs">New user</Badge>
                  </HStack>
                </FormLabel>
                <HStack>
                  <NumberInput
                    value={referralSettings.refereeRewardValue}
                    onChange={(_, val) => setReferralSettings({ ...referralSettings, refereeRewardValue: val || 0 })}
                    min={0}
                    flex={1}
                  >
                    <NumberInputField borderRadius="xl" borderColor="gray.300" bg="white" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Badge colorScheme="purple" fontSize="sm" px={3} py={2}>
                    {referralSettings.rewardType === 'discount_percentage' ? '%' : 'TZS'}
                  </Badge>
                </HStack>
              </FormControl>
            </SimpleGrid>

            {/* Require first payment toggle */}
            <Flex justify="space-between" align="center">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" fontWeight="medium" color="gray.900">Require First Payment to Unlock Reward</Text>
                <Text fontSize="xs" color="gray.500">
                  Recommended: prevents abuse by requiring the new user to make a real booking first
                </Text>
              </VStack>
              <Switch
                isChecked={referralSettings.requireFirstPayment}
                onChange={(e) => setReferralSettings({ ...referralSettings, requireFirstPayment: e.target.checked })}
                colorScheme="brand"
                size="lg"
              />
            </Flex>

            <Divider />

            {/* Program description */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium">Program Description (shown to users)</FormLabel>
              <Input
                value={referralSettings.programDescription}
                onChange={(e) => setReferralSettings({ ...referralSettings, programDescription: e.target.value })}
                borderRadius="xl"
                borderColor="gray.300"
                bg="white"
              />
              <FormHelperText fontSize="xs">Displayed on the Promotions page and Profile page in the mobile app</FormHelperText>
            </FormControl>

            {/* Preview */}
            <Alert status="info" borderRadius="xl">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <AlertTitle fontSize="sm">How it works
                </AlertTitle>
                <AlertDescription fontSize="xs">
                  1. Every user gets a unique referral code automatically<br />
                  2. They share it with friends<br />
                  3. New user enters code during signup<br />
                  4. After the new user&apos;s first paid trip, <strong>both</strong> receive their reward<br />
                  5. Referrer gets <strong>{referralSettings.rewardType === 'free_trip' ? 'a Free Trip' : `${referralSettings.referrerRewardValue}${referralSettings.rewardType === 'discount_percentage' ? '%' : ' TZS'}`}</strong>, New user gets <strong>{referralSettings.rewardType === 'free_trip' ? 'a Free Trip' : `${referralSettings.refereeRewardValue}${referralSettings.rewardType === 'discount_percentage' ? '%' : ' TZS'}`}</strong>
                </AlertDescription>
              </VStack>
            </Alert>

          </VStack>
        </CardBody>
      </Card>

      {/* Payment Method Prefixes */}
      <Card variant="glass">
        <CardHeader>
          <HStack justify="space-between">
            <HStack spacing={3}>
              <Box p={2} bg="blue.100" borderRadius="lg">
                <Smartphone size={20} color="var(--chakra-colors-blue-600)" />
              </Box>
              <Heading size="md" color="gray.900">
                Payment Method Detection
              </Heading>
            </HStack>
            <Button
              leftIcon={<Plus size={16} />}
              colorScheme="brand"
              size="sm"
              borderRadius="xl"
              onClick={() => {
                setEditingPrefix(null)
                setPrefixForm({ prefix: '', paymentMethod: 'mpesa', operator: '' })
                onPrefixModalOpen()
              }}
            >
              Add Prefix
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" borderRadius="xl" variant="subtle">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <AlertTitle fontSize="sm">Auto-Detection</AlertTitle>
                <AlertDescription fontSize="xs">
                  The system automatically detects payment methods based on the first 3 digits of the phone number.
                  Unknown prefixes will be saved as &quot;unknown&quot; payment method.
                </AlertDescription>
              </VStack>
            </Alert>

            {prefixesLoading ? (
              <Progress size="xs" isIndeterminate colorScheme="brand" borderRadius="full" />
            ) : (
              <Box overflowX="auto">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4A5568' }}>Prefix</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4A5568' }}>Operator</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4A5568' }}>Payment Method</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4A5568' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#4A5568' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(paymentPrefixes?.data ?? []).map((prefix: any) => (
                      <tr key={prefix.id} style={{ borderBottom: '1px solid #F7FAFC' }}>
                        <td style={{ padding: '12px', fontSize: '14px', fontWeight: 600 }}>{prefix.prefix}</td>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{prefix.operator}</td>
                        <td style={{ padding: '12px', fontSize: '14px' }}>
                          <Badge colorScheme={prefix.paymentMethod === 'mpesa' ? 'green' : prefix.paymentMethod === 'airtelmoney' ? 'red' : 'blue'} borderRadius="md">
                            {prefix.paymentMethod}
                          </Badge>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <Badge colorScheme={prefix.active ? 'green' : 'gray'} borderRadius="md">
                            {prefix.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <HStack spacing={2} justify="flex-end">
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              leftIcon={<Edit size={12} />}
                              borderRadius="lg"
                              onClick={() => {
                                setEditingPrefix(prefix)
                                setPrefixForm({ prefix: prefix.prefix, paymentMethod: prefix.paymentMethod, operator: prefix.operator })
                                onPrefixModalOpen()
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              leftIcon={<Trash2 size={12} />}
                              borderRadius="lg"
                              onClick={() => {
                                if (window.confirm(`Delete prefix ${prefix.prefix}?`)) {
                                  deletePrefixMutation.mutate(prefix.id)
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </HStack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!paymentPrefixes?.data || paymentPrefixes.data.length === 0) && (
                  <Text textAlign="center" color="gray.500" py={8} fontSize="sm">
                    No payment prefixes configured. Click &quot;Add Prefix&quot; to get started.
                  </Text>
                )}
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Advanced Settings */}
      <Card variant="glass">
        <CardHeader>
          <HStack spacing={3}>
            <Box p={2} bg="gray.100" borderRadius="lg">
              <Shield size={20} color="var(--chakra-colors-gray-600)" />
            </Box>
            <Heading size="md" color="gray.900">
              Advanced Settings
            </Heading>
          </HStack>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            <Flex justify="space-between" align="center">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                  Auto Backup
                </Text>
                <Text fontSize="xs" color="gray.600">
                  Automatically create daily backups
                </Text>
              </VStack>
              <Switch
                isChecked={settings.autoBackup}
                onChange={(e) => setSettings({...settings, autoBackup: e.target.checked})}
                colorScheme="brand"
                size="lg"
              />
            </Flex>
            
            <Flex justify="space-between" align="center">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                  Cache Enabled
                </Text>
                <Text fontSize="xs" color="gray.600">
                  Enable system caching for better performance
                </Text>
              </VStack>
              <Switch
                isChecked={settings.cacheEnabled}
                onChange={(e) => setSettings({...settings, cacheEnabled: e.target.checked})}
                colorScheme="brand"
                size="lg"
              />
            </Flex>
            
            <Flex justify="space-between" align="center">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                  Debug Mode
                </Text>
                <Text fontSize="xs" color="gray.600">
                  Enable debug logging (Development only)
                </Text>
              </VStack>
              <Switch
                isChecked={settings.debugMode}
                onChange={(e) => setSettings({...settings, debugMode: e.target.checked})}
                colorScheme="brand"
                size="lg"
              />
            </Flex>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Maintenance Mode Confirmation Modal */}
      <Modal isOpen={isMaintenanceOpen} onClose={onMaintenanceClose}>
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader>
            <HStack spacing={3}>
              <Box p={2} bg="red.100" borderRadius="lg">
                <AlertTriangle size={20} color="var(--chakra-colors-red-600)" />
              </Box>
              <Text fontSize="xl" fontWeight="bold">
                {settings.maintenanceMode ? 'Disable' : 'Enable'} Maintenance Mode
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" borderRadius="xl" mb={4}>
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <AlertTitle fontSize="sm">Warning!</AlertTitle>
                <AlertDescription fontSize="sm">
                  {settings.maintenanceMode 
                    ? 'Disabling maintenance mode will make the system accessible to all users again.'
                    : 'Enabling maintenance mode will make the system inaccessible to regular users. Only administrators will be able to access the system.'
                  }
                </AlertDescription>
              </VStack>
            </Alert>
            <Text fontSize="sm" color="gray.600">
              Are you sure you want to {settings.maintenanceMode ? 'disable' : 'enable'} maintenance mode?
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="outline" onClick={onMaintenanceClose} borderRadius="xl">
                Cancel
              </Button>
              <Button
                colorScheme={settings.maintenanceMode ? "green" : "red"}
                onClick={confirmMaintenanceMode}
                borderRadius="xl"
              >
                {settings.maintenanceMode ? 'Disable' : 'Enable'} Maintenance Mode
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Payment Prefix Modal */}
      <Modal isOpen={isPrefixModalOpen} onClose={onPrefixModalClose} size="md">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader>
            <HStack spacing={3}>
              <Box p={2} bg="blue.100" borderRadius="lg">
                <Smartphone size={20} color="var(--chakra-colors-blue-600)" />
              </Box>
              <Text fontSize="xl" fontWeight="bold">
                {editingPrefix ? 'Edit' : 'Add'} Payment Prefix
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Prefix (3 digits)</FormLabel>
                <Input
                  placeholder="e.g. 074"
                  value={prefixForm.prefix}
                  onChange={(e) => setPrefixForm({ ...prefixForm, prefix: e.target.value })}
                  maxLength={3}
                  borderRadius="xl"
                />
                <FormHelperText fontSize="xs">First 3 digits of phone number</FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Payment Method</FormLabel>
                <Select
                  value={prefixForm.paymentMethod}
                  onChange={(e) => setPrefixForm({ ...prefixForm, paymentMethod: e.target.value })}
                  borderRadius="xl"
                >
                  <option value="mpesa">M-Pesa</option>
                  <option value="airtelmoney">Airtel Money</option>
                  <option value="tigopesa">Tigo Pesa</option>
                  <option value="halopesa">Halo Pesa</option>
                  <option value="zenopay">Zeno Pay</option>
                  <option value="wallet">Wallet</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Operator Name</FormLabel>
                <Input
                  placeholder="e.g. Vodacom Tanzania"
                  value={prefixForm.operator}
                  onChange={(e) => setPrefixForm({ ...prefixForm, operator: e.target.value })}
                  borderRadius="xl"
                />
                <FormHelperText fontSize="xs">Mobile network operator name</FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="outline" onClick={onPrefixModalClose} borderRadius="xl">
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                onClick={() => {
                  if (editingPrefix) {
                    updatePrefixMutation.mutate({ id: editingPrefix.id, data: prefixForm })
                  } else {
                    createPrefixMutation.mutate(prefixForm)
                  }
                }}
                isLoading={createPrefixMutation.isLoading || updatePrefixMutation.isLoading}
                borderRadius="xl"
                isDisabled={!prefixForm.prefix || !prefixForm.operator || prefixForm.prefix.length !== 3}
              >
                {editingPrefix ? 'Update' : 'Add'} Prefix
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
